import { useEffect, useState } from 'react';
import { Loader2, Users, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getProjoPersonnelForProject,
  type ProjoPersonnel,
} from '@/services/db/projo';
import { savePersonnelRow } from '@/services/db/dsrEntry';

interface Props {
  open: boolean;
  onClose: () => void;
  projoProjectId: string;
  dsrId: string;
  tenantId: string;
  reportDate?: string;
  onImported: () => void;
}

export default function ProjoImportModal({
  open,
  onClose,
  projoProjectId,
  dsrId,
  tenantId,
  reportDate,
  onImported,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState<ProjoPersonnel[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getProjoPersonnelForProject(projoProjectId, reportDate)
      .then(rows => {
        setPeople(rows);
        setSelected(new Set(rows.map(r => r.personnel_id)));
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load PROJO personnel'))
      .finally(() => setLoading(false));
  }, [open, projoProjectId, reportDate]);

  if (!open) return null;

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doImport = async () => {
    setImporting(true);
    try {
      // Group selected personnel by employer_name, create one workforce
      // entry per employer. Headcount = count; SSE names → notes; SSE
      // count → supervisor_name slot so the existing SSE% calc picks it
      // up (supervisor_name presence == an SSE is named).
      const picked = people.filter(p => selected.has(p.personnel_id));
      const groups = new Map<string, ProjoPersonnel[]>();
      for (const p of picked) {
        const key = p.employer_name ?? 'Unknown Employer';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(p);
      }

      for (const [employer, members] of groups) {
        const sseNames = members
          .filter(m => m.is_sse)
          .map(m => `${m.first_name} ${m.last_name}`);
        const notes = [
          `Imported from PROJO (${members.length} personnel)`,
          sseNames.length ? `SSE: ${sseNames.join(', ')}` : null,
        ]
          .filter(Boolean)
          .join(' · ');

        await savePersonnelRow({
          dsrReportId: dsrId,
          tenantId,
          companyName: employer,
          crewType: 'contractor',
          personnelCount: members.length,
          supervisorName: sseNames.length > 0 ? sseNames[0] : null,
          notes,
        });
      }
      onImported();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b px-6 py-4">
          <Users className="h-5 w-5 text-mozaic-teal" />
          <h2 className="font-semibold text-mozaic-navy">Import from PROJO Registry</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-mozaic-teal" />
            </div>
          )}
          {error && <p className="text-sm text-mozaic-red">{error}</p>}
          {!loading && !error && people.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No active PROJO assignments for this project on this date.
            </p>
          )}
          {!loading && people.length > 0 && (
            <ul className="divide-y">
              {people.map(p => {
                const isSelected = selected.has(p.personnel_id);
                return (
                  <li key={p.personnel_id}>
                    <button
                      type="button"
                      onClick={() => toggle(p.personnel_id)}
                      className="flex w-full items-center gap-3 py-3 text-left hover:bg-muted/50"
                    >
                      <div
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? 'border-mozaic-teal bg-mozaic-teal text-white'
                            : 'border-input bg-background'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium text-mozaic-navy">
                            {p.first_name} {p.last_name}
                          </span>
                          {p.is_sse && (
                            <Badge className="bg-amber-500 text-white">SSE</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {[p.position_name, p.employer_name, p.worker_type]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {selected.size} of {people.length} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={importing}>
              Cancel
            </Button>
            <Button
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
              onClick={doImport}
              disabled={importing || selected.size === 0}
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import Selected (${selected.size})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
